'use client'

import { useEffect, useRef, useState } from 'react'

interface Alert {
  patientId: string
  patientName: string
  room: string
  riskScore: number
  severity: 'critical' | 'warning' | 'info'
  message: string
  timestamp: string
}

interface AlertSettings {
  soundEnabled: boolean
  nightShiftOnly: boolean
  snoozeDuration: number // minutes
}

const DEFAULT_SETTINGS: AlertSettings = {
  soundEnabled: true,
  nightShiftOnly: true,
  snoozeDuration: 5
}

export function useAlerts() {
  const [settings, setSettings] = useState<AlertSettings>(DEFAULT_SETTINGS)
  const [snoozedAlerts, setSnoozedAlerts] = useState<Set<string>>(new Set())
  const eventSourceRef = useRef<EventSource | null>(null)
  const audioContextRef = useRef<AudioContext | null>(null)

  // Initialize sound
  useEffect(() => {
    // Use Web Audio API for simple beep sound
    if (typeof window !== 'undefined' && window.AudioContext) {
      audioContextRef.current = new AudioContext()
    }

    // Load settings from localStorage
    const savedSettings = localStorage.getItem('alertSettings')
    if (savedSettings) {
      try {
        setSettings(JSON.parse(savedSettings))
      } catch (error) {
        console.error('Failed to parse alert settings:', error)
      }
    }
  }, [])

  // Check if it's night shift (21:00 - 06:00)
  const isNightShift = () => {
    const hour = new Date().getHours()
    return hour >= 21 || hour < 6
  }

  // Play alert sound using audio file
  const playSound = () => {
    console.log('playSound called, soundEnabled:', settings.soundEnabled)

    if (settings.soundEnabled) {
      if (!settings.nightShiftOnly || isNightShift()) {
        try {
          // Use the alert.mp3 file instead of Web Audio API
          const audio = new Audio('/sounds/alert.mp3')
          audio.volume = 0.6

          console.log('Attempting to play alert sound...')
          audio.play()
            .then(() => {
              console.log('Alert sound played successfully!')
            })
            .catch(err => {
              console.error('Alert sound failed:', err)
              // If autoplay is blocked, try Web Audio API as fallback
              if (err.name === 'NotAllowedError' && audioContextRef.current) {
                console.log('Falling back to Web Audio API beep')
                try {
                  const oscillator = audioContextRef.current.createOscillator()
                  const gainNode = audioContextRef.current.createGain()

                  oscillator.connect(gainNode)
                  gainNode.connect(audioContextRef.current.destination)

                  oscillator.frequency.value = 800
                  oscillator.type = 'sine'

                  gainNode.gain.setValueAtTime(0.3, audioContextRef.current.currentTime)
                  gainNode.gain.exponentialRampToValueAtTime(0.01, audioContextRef.current.currentTime + 0.5)

                  oscillator.start(audioContextRef.current.currentTime)
                  oscillator.stop(audioContextRef.current.currentTime + 0.5)
                } catch (fallbackError) {
                  console.error('Web Audio API fallback also failed:', fallbackError)
                }
              }
            })
        } catch (error) {
          console.error('Failed to play sound:', error)
        }
      } else {
        console.log('Not playing sound: nightShiftOnly is true and not in night shift')
      }
    } else {
      console.log('Not playing sound: soundEnabled is false')
    }
  }

  // Snooze an alert
  const snoozeAlert = (alertKey: string) => {
    setSnoozedAlerts(prev => new Set(prev).add(alertKey))

    // Remove from snoozed after duration
    setTimeout(() => {
      setSnoozedAlerts(prev => {
        const next = new Set(prev)
        next.delete(alertKey)
        return next
      })
    }, settings.snoozeDuration * 60 * 1000)

    // No toast - all notifications handled by AlertBanner
    console.log(`Alert snoozed for ${settings.snoozeDuration} minutes`)
  }

  // Show toast notification - DISABLED: Only use header alerts
  const showNotification = (alert: Alert) => {
    // Check if alert is snoozed
    const alertKey = `${alert.patientId}-${alert.severity}`
    if (snoozedAlerts.has(alertKey)) {
      return
    }

    // Only play sound, no toast notifications
    // All notifications now show only in the header AlertBanner
    if (alert.severity !== 'info') {
      playSound()
    }

    // Log for debugging
    console.log('Alert received (header only):', alert)
  }

  // Connect to SSE
  useEffect(() => {
    const connectSSE = () => {
      try {
        eventSourceRef.current = new EventSource('/api/v1/events/stream')

        eventSourceRef.current.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data)

            // Convert to Alert format
            const alert: Alert = {
              patientId: data.patientId,
              patientName: data.patientName || '환자',
              room: data.room || '미지정',
              riskScore: Math.round(data.riskScore * 100),
              severity: data.riskScore > 0.75 ? 'critical' :
                       data.riskScore > 0.5 ? 'warning' : 'info',
              message: data.message || `호흡 억제 위험도가 ${Math.round(data.riskScore * 100)}%로 상승했습니다`,
              timestamp: data.timestamp
            }

            showNotification(alert)
          } catch (error) {
            console.error('Failed to parse SSE message:', error)
          }
        }

        eventSourceRef.current.onerror = () => {
          // SSE connection error는 정상적으로 발생할 수 있으므로 에러 로그 제거
          eventSourceRef.current?.close()
          setTimeout(connectSSE, 5000)
        }
      } catch (error) {
        console.error('Failed to connect to SSE:', error)
        setTimeout(connectSSE, 5000)
      }
    }

    connectSSE()

    return () => {
      eventSourceRef.current?.close()
    }
  }, [settings, snoozedAlerts])

  // Update settings
  const updateSettings = (newSettings: Partial<AlertSettings>) => {
    const updated = { ...settings, ...newSettings }
    setSettings(updated)
    localStorage.setItem('alertSettings', JSON.stringify(updated))
  }

  return {
    settings,
    updateSettings,
    testAlert: () => {
      console.log('Test Alert button clicked!')
      const testAlert: Alert = {
        patientId: 'TEST',
        patientName: '테스트 환자',
        room: '999',
        riskScore: 85,
        severity: 'critical',
        message: '테스트 경보입니다. 실제 경보가 아닙니다.',
        timestamp: new Date().toISOString()
      }
      showNotification(testAlert)
      // Also directly try to play sound for testing
      console.log('Directly playing sound for test...')
      playSound()
    }
  }
}